import { tx as translateInterfaceText } from '@/utils/i18n';
/**
 * Especificação declarativa dos 8 modelos de bloco de oferta.
 *
 * Cada modelo declara **o que desenha e em que ordem** (`layout`) e **com que
 * conteúdo nasce** (`defaults`). Editor e render consultam a mesma tabela: o
 * editor mostra os campos dos slots presentes no layout, o render percorre o
 * layout desenhando slot a slot. Nenhum dos dois carrega `if (model === ...)`.
 *
 * Adicionar um modelo novo = adicionar uma entrada aqui.
 */

import type {
  ActionButtonModel,
  ActionButtonModelSnapshot,
  ActionButtonPrimarySnapshot,
  ActionButtonTemplate,
} from '@/store/videoFeatures/types/action-button-templates';
import type { ActionButtonTextElementKey } from './tokens';

/** Um slot é qualquer coisa que o bloco pode desenhar, na ordem visual. */
export type ActionButtonSlot =
  | ActionButtonTextElementKey
  | 'progressBar'
  | 'buttons';

export interface ActionButtonModelSpec {
  id: ActionButtonModel;
  label: string;
  /** Uma linha explicando quando usar. Aparece no seletor. */
  description: string;
  /** O que o modelo desenha, na ordem visual de cima para baixo. */
  layout: ActionButtonSlot[];
  /** Forma do segundo caminho. `null` = o modelo não tem segundo caminho. */
  secondaryAs: 'button' | 'link' | null;
  /** Quando há dois botões, ficam na mesma linha em vez de empilhados. */
  buttonsInline: boolean;
  /** Conteúdo e cores com que o modelo nasce. */
  defaults: ActionButtonModelSnapshot;
}

// Paleta base. Escolhida para funcionar sobre fundo claro — o bloco vive na
// página do cliente, cujo fundo não controlamos, e o caso comum é claro.
const INK = '#0F172A';
const INK_SOFT = '#475569';
const INK_MUTED = '#64748B';
const INK_FAINT = '#94A3B8';
const BRAND = '#1D4FFF';
const BRAND_HOVER = '#1A47E5';
const SUCCESS = '#0D9488';
const MONEY = '#10B981';
const DANGER = '#DC2626';
const SURFACE = '#FFFFFF';
const SURFACE_SOFT = '#F1F5F9';
const BORDER = '#E2E8F0';

/** Botão primário padrão — herdado por todos os modelos. */
// Sem `url`: o link de destino é do vídeo, não do modelo. Semear '' aqui fazia
// a primeira troca de modelo esvaziar o checkout já configurado.
const basePrimary: ActionButtonPrimarySnapshot = {
  size: 'medium',
  backgroundColor: BRAND,
  textColor: SURFACE,
  hoverBackgroundColor: BRAND_HOVER,
  hoverTextColor: SURFACE,
  borderRadius: 8,
  shadow: true,
  pulse: false,
};

/** Link de recusa padrão — discreto, abaixo do botão. */
const baseLinkSecondary = {
  enabled: true,
  renderAs: 'link' as const,
  url: '',
  openInNewTab: false,
  size: 'medium' as const,
  weight: 'normal' as const,
  color: INK_MUTED,
  hoverColor: DANGER,
  underline: true,
};

/** Botão de recusa padrão — usado quando as duas opções têm o mesmo peso. */
const baseButtonSecondary = {
  enabled: true,
  renderAs: 'button' as const,
  url: '',
  openInNewTab: false,
  size: 'medium' as const,
  weight: 'semibold' as const,
  color: INK,
  backgroundColor: SURFACE_SOFT,
  borderColor: BORDER,
  hoverColor: INK,
  hoverBackgroundColor: '#E2E8F0',
  hoverBorderColor: '#CBD5E1',
};

export const ACTION_BUTTON_MODELS: ActionButtonModelSpec[] = [
  {
    id: 'default',
    get label() { return translateInterfaceText("Padrão"); },
    get description() { return translateInterfaceText("Só o botão. É o comportamento clássico."); },
    layout: ['headline', 'subheadline', 'buttons'],
    secondaryAs: null,
    buttonsInline: false,
    defaults: {
      headline: { text: '', color: INK, size: 'large', weight: 'bold' },
      subheadline: { text: '', color: INK_SOFT, size: 'medium', weight: 'normal' },
      secondary: { enabled: false },
      primary: { ...basePrimary, text: "QUERO CONTINUAR" },
    },
  },
  {
    id: 'upsell-downsell',
    get label() { return translateInterfaceText("Upsell + Downsell"); },
    get description() { return translateInterfaceText("Botão de aceite com um link de recusa discreto abaixo."); },
    layout: ['headline', 'subheadline', 'buttons'],
    secondaryAs: 'link',
    buttonsInline: false,
    defaults: {
      headline: {
        text: "Espere! Temos uma condição especial para você",
        color: INK,
        size: 'large',
        weight: 'bold',
      },
      subheadline: {
        text: "Adicione essa oferta ao seu pedido antes de continuar",
        color: INK_SOFT,
        size: 'medium',
        weight: 'normal',
      },
      secondary: {
        ...baseLinkSecondary,
        text: "Não, obrigado. Quero continuar sem essa oferta",
      },
      primary: { ...basePrimary, text: "SIM, QUERO APROVEITAR" },
    },
  },
  {
    id: 'yes-no',
    get label() { return translateInterfaceText("Sim ou Não"); },
    get description() { return translateInterfaceText("Dois botões lado a lado, com o mesmo peso visual."); },
    layout: ['headline', 'subheadline', 'buttons'],
    secondaryAs: 'button',
    buttonsInline: true,
    defaults: {
      headline: {
        text: "Deseja aproveitar essa oportunidade?",
        color: INK,
        size: 'large',
        weight: 'bold',
      },
      subheadline: {
        text: "Escolha uma opção abaixo para continuar",
        color: INK_SOFT,
        size: 'medium',
        weight: 'normal',
      },
      secondary: { ...baseButtonSecondary, text: "NÃO, OBRIGADO" },
      primary: { ...basePrimary, text: "SIM, EU QUERO" },
    },
  },
  {
    id: 'dupla-escolha',
    get label() { return translateInterfaceText("Dupla Escolha"); },
    get description() { return translateInterfaceText("Dois botões empilhados, para opções com textos longos."); },
    layout: ['headline', 'subheadline', 'buttons'],
    secondaryAs: 'button',
    buttonsInline: false,
    defaults: {
      headline: {
        text: "Como você deseja continuar?",
        color: INK,
        size: 'large',
        weight: 'bold',
      },
      subheadline: {
        text: "Escolha uma das opções abaixo",
        color: INK_SOFT,
        size: 'medium',
        weight: 'normal',
      },
      secondary: { ...baseButtonSecondary, text: "CONTINUAR SEM ESSA OFERTA" },
      primary: { ...basePrimary, text: "QUERO APROVEITAR A OFERTA" },
    },
  },
  {
    id: 'offer',
    get label() { return translateInterfaceText("Oferta Especial"); },
    get description() { return translateInterfaceText("Chamada de oferta com botão e link de saída."); },
    layout: ['headline', 'subheadline', 'buttons'],
    secondaryAs: 'link',
    buttonsInline: false,
    defaults: {
      headline: {
        text: "OFERTA ESPECIAL PARA VOCÊ",
        color: INK,
        size: 'large',
        weight: 'extra-bold',
      },
      subheadline: {
        text: "Essa condição está disponível somente nesta página",
        color: INK_SOFT,
        size: 'medium',
        weight: 'normal',
      },
      secondary: {
        ...baseLinkSecondary,
        text: "Não, obrigado. Quero continuar",
      },
      primary: { ...basePrimary, text: "QUERO CONTINUAR" },
    },
  },
  {
    id: 'upsell-price',
    get label() { return translateInterfaceText("Oferta com Preço"); },
    get description() { return translateInterfaceText("Mostra o preço em destaque com a condição de pagamento."); },
    layout: ['headline', 'subheadline', 'offerText', 'price', 'priceNote', 'buttons'],
    secondaryAs: 'link',
    buttonsInline: false,
    defaults: {
      headline: {
        text: "Aproveite essa condição especial",
        color: INK,
        size: 'large',
        weight: 'bold',
      },
      subheadline: {
        text: "Disponível somente nesta página",
        color: INK_SOFT,
        size: 'medium',
        weight: 'normal',
      },
      offerText: {
        text: "Adicione essa oferta ao seu pedido agora e aproveite por apenas:",
        color: INK_SOFT,
        size: 'medium',
        weight: 'normal',
      },
      price: { text: '12x de R$ 12,89', color: BRAND, size: 'large', weight: 'extra-bold' },
      priceNote: {
        text: "ou R$ 154,68 à vista",
        color: INK_MUTED,
        size: 'medium',
        weight: 'normal',
      },
      secondary: {
        ...baseLinkSecondary,
        text: "Não, obrigado. Quero continuar sem essa oferta",
      },
      primary: { ...basePrimary, text: "SIM, QUERO ADICIONAR" },
    },
  },
  {
    id: 'highlighted-offer',
    get label() { return translateInterfaceText("Oferta com Riscado"); },
    get description() { return translateInterfaceText("Preço antigo riscado e o novo em destaque — ancoragem de valor."); },
    layout: ['headline', 'subheadline', 'strikethrough', 'featured', 'buttons'],
    secondaryAs: 'link',
    buttonsInline: false,
    defaults: {
      headline: {
        text: "Espere! Oferta especial",
        color: INK,
        size: 'large',
        weight: 'extra-bold',
      },
      subheadline: {
        text: "OFERTA ÚNICA!",
        color: SUCCESS,
        size: 'large',
        weight: 'extra-bold',
      },
      strikethrough: {
        enabled: true,
        text: "Valor total: R$ 999",
        color: INK_FAINT,
        size: 'large',
        weight: 'semibold',
      },
      featured: {
        text: "Seu preço hoje: R$ 99",
        color: SUCCESS,
        size: 'large',
        weight: 'bold',
      },
      secondary: {
        ...baseLinkSecondary,
        text: "Não, obrigado. Quero continuar sem essa oferta",
        underline: false,
      },
      primary: {
        ...basePrimary,
        text: "SIM, ADICIONAR AO MEU PEDIDO",
        backgroundColor: SUCCESS,
        hoverBackgroundColor: '#0F766E',
      },
    },
  },
  {
    id: 'urgency-progress',
    get label() { return translateInterfaceText("Oferta com Progresso"); },
    get description() { return translateInterfaceText("Faixa de urgência e barra de progresso acima da oferta."); },
    layout: [
      'urgency',
      'progressBar',
      'headline',
      'subheadline',
      'price',
      'priceNote',
      'buttons',
    ],
    secondaryAs: 'link',
    buttonsInline: false,
    defaults: {
      urgency: {
        enabled: true,
        text: "⚠️ ÚLTIMA CHANCE!",
        color: DANGER,
        size: 'medium',
        weight: 'extra-bold',
      },
      progressBar: {
        enabled: true,
        percentage: 80,
        showText: true,
        text: '80% das vagas preenchidas',
        trackColor: BORDER,
        fillColor: DANGER,
        textColor: SURFACE,
      },
      headline: {
        text: "Garanta essa condição especial agora",
        color: INK,
        size: 'large',
        weight: 'bold',
      },
      subheadline: {
        text: "Oferta disponível somente nesta página",
        color: INK_SOFT,
        size: 'medium',
        weight: 'normal',
      },
      price: { text: '12x de R$ 12,89', color: MONEY, size: 'large', weight: 'extra-bold' },
      priceNote: {
        text: "ou R$ 154,68 à vista",
        color: INK_MUTED,
        size: 'medium',
        weight: 'normal',
      },
      secondary: {
        ...baseLinkSecondary,
        text: "Não, obrigado. Quero continuar sem essa oferta",
      },
      primary: { ...basePrimary, text: "SIM, QUERO APROVEITAR" },
    },
  },
];

/** Índice por id, para lookup direto. */
export const MODEL_SPEC = ACTION_BUTTON_MODELS.reduce(
  (acc, spec) => {
    acc[spec.id] = spec;
    return acc;
  },
  {} as Record<ActionButtonModel, ActionButtonModelSpec>,
);

/** Spec de um modelo, caindo em `default` para valor desconhecido/legado. */
export function modelSpec(model: ActionButtonModel | undefined): ActionButtonModelSpec {
  return MODEL_SPEC[model as ActionButtonModel] ?? MODEL_SPEC.default;
}

/** O modelo desenha este slot? */
export function hasSlot(model: ActionButtonModel | undefined, slot: ActionButtonSlot): boolean {
  return modelSpec(model).layout.includes(slot);
}

/**
 * Defaults de um modelo, em cópia profunda — o chamador grava direto no estado
 * sem risco de compartilhar referência com a tabela.
 */
export function modelDefaults(model: ActionButtonModel): ActionButtonModelSnapshot {
  return structuredClone(modelSpec(model).defaults);
}

/** Bloco de oferta com que um botão de ação novo nasce. */
export function defaultTemplate(): ActionButtonTemplate {
  const { primary: _primary, ...rest } = modelDefaults('default');
  return { ...rest, model: 'default' };
}
